import { useEffect, useState } from "react";

interface TutorialArrowProps {
	targetSelector: string;
	cardPosition: string;
}

export function TutorialArrow({ targetSelector, cardPosition }: TutorialArrowProps) {
	const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

	useEffect(() => {
		function update() {
			const el = document.querySelector(targetSelector);
			if (el) {
				setTargetRect(el.getBoundingClientRect());
			} else {
				setTargetRect(null);
			}
		}

		update();

		// Re-compute on resize or scroll
		const observer = new ResizeObserver(update);
		const el = document.querySelector(targetSelector);
		if (el) observer.observe(el);

		window.addEventListener("resize", update);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", update);
		};
	}, [targetSelector]);

	if (!targetRect) return null;

	// Position the arrow to point at the target center
	const targetCx = targetRect.left + targetRect.width / 2;
	const targetCy = targetRect.top + targetRect.height / 2;

	// Determine arrow start offset based on card position
	let startX = targetCx;
	let startY = targetCy;
	const offset = 50;

	if (cardPosition.includes("bottom")) {
		startY = targetCy + targetRect.height / 2 + offset;
	} else if (cardPosition.includes("top")) {
		startY = targetCy - targetRect.height / 2 - offset;
	}
	if (cardPosition.includes("left")) {
		startX = targetCx - targetRect.width / 2 - offset;
	} else if (cardPosition.includes("right")) {
		startX = targetCx + targetRect.width / 2 + offset;
	}

	// Arrow endpoint is the target edge
	const endX = targetCx;
	const endY = targetRect.top + targetRect.height + 4;

	return (
		<div
			className="tutorial-arrow"
			style={{
				left: Math.min(startX, endX) - 10,
				top: Math.min(startY, endY) - 10,
				width: Math.abs(endX - startX) + 20,
				height: Math.abs(endY - startY) + 20,
			}}
		>
			<svg
				className="tutorial-arrow__svg"
				width="100%"
				height="100%"
				viewBox={`0 0 ${Math.abs(endX - startX) + 20} ${Math.abs(endY - startY) + 20}`}
			>
				<line
					className="tutorial-arrow__path"
					x1={startX < endX ? 10 : Math.abs(endX - startX) + 10}
					y1={startY < endY ? 10 : Math.abs(endY - startY) + 10}
					x2={startX < endX ? Math.abs(endX - startX) + 10 : 10}
					y2={startY < endY ? Math.abs(endY - startY) + 10 : 10}
				/>
				{/* Arrowhead */}
				<circle
					className="tutorial-arrow__head"
					cx={startX < endX ? Math.abs(endX - startX) + 10 : 10}
					cy={startY < endY ? Math.abs(endY - startY) + 10 : 10}
					r={4}
				/>
			</svg>
		</div>
	);
}
